import * as React from 'react';
import './Alert.scss';
import { DateTime } from 'luxon';
import { createAlert, fetchProductInfos, getAlert, updateAlert } from '../../services/ProductAlertService';
import { Redirect } from 'react-router-dom';
import { AlertType, Disposition, ProductAlert, ProductInfo, RawAlert, User, UserType } from '../../types';
import { MdArrowBack } from 'react-icons/lib/md';
import { TiPencil } from 'react-icons/lib/ti';
import { LoadHolder } from '../../components/Loader';
import Modal from '../../components/Modal';
import { UserService } from '../../services/ApiService';
import ImageUploader from '../../components/ImageUploader';
import { savePhoto } from '../../services/ProductAlertService';

export type AlertProps = {
  match?: any;
};

type AlertState = {
  loading: boolean;
  itemLoading: boolean;
  error: string;
  isNew: boolean;
  id: string | undefined;
  alertNumber: number | undefined;
  active: boolean | undefined;
  expiryDate: DateTime | undefined;
  isGroceryExclusive: boolean | undefined,
  groceryExclusiveItems: ProductInfo[],
  customSize: string,
  customItemName: string,
  customItemNumber: string,
  customImage: File | undefined,
  customImageUrl: string | undefined,
  itemInfo: ProductInfo[];
  itemError: string;
  newItems: ProductInfo[];
  missingItems: string[];
  missingFields: string[];
  type: AlertType | undefined;
  created: DateTime | undefined;
  updated: DateTime | undefined;
  lotCodes: string[];
  additionalInfo: string | undefined;
  isArchived: boolean;
  disposition: Disposition;
  edit: boolean;
  redirectToAlert: string | boolean;
  redirectToAllAlerts: boolean;
  showBackModal: boolean;
  user: User | undefined;
  itemNumbers?: number[] | undefined;
};

export default class Alert extends React.Component<AlertProps, AlertState> {
  constructor(props: AlertProps) {
    super(props);
    this.state = {
      error: '',
      isNew: false,
      loading: false,
      itemLoading: false,
      id: undefined,
      alertNumber: undefined,
      active: undefined,
      expiryDate: DateTime.local().plus({days: 7}),
      isGroceryExclusive: false,
      groceryExclusiveItems: [],
      customSize: '',
      customItemName: '',
      customItemNumber: '',
      customImage: undefined,
      customImageUrl: undefined,
      itemInfo: [{itemName: '', itemNumber: '', imageUrl: '', imageUrlThumb: '', productSize: ''}],
      newItems: [{itemName: '', itemNumber: '', imageUrl: '', imageUrlThumb: '', productSize: ''}],
      missingItems: [],
      missingFields: [],
      itemError: '',
      type: undefined,
      created: DateTime.local(),
      updated: DateTime.local(),
      edit: false,
      additionalInfo: '',
      lotCodes: [],
      isArchived: false,
      disposition: 'both',
      itemNumbers: [],
      redirectToAlert: false,
      redirectToAllAlerts: false,
      showBackModal: false,
      user: undefined,
    };
    // binding functions
  }

  setStatus = (e) => {
    this.setState({ active: e.target.value === 'true' });
  }

  setDisposition = (e) => {
    this.setState({ disposition: e.target.value });
  }

  toggleEdit = async () => {
    let {
      edit,
      isNew,
      alertNumber,
      isGroceryExclusive,
      customItemName,
      customItemNumber,
      customSize,
      customImage,
      customImageUrl,
      groceryExclusiveItems,
      additionalInfo,
      created,
      expiryDate,
      itemInfo,
      active,
      type,
      lotCodes,
      isArchived,
      disposition
    } = this.state;
    if (!edit) {
      this.setState({edit: true});
    } else if (created && expiryDate && created > expiryDate) {
      this.setState({
        missingFields: [
          'Alert Expiry Date'
        ],
        error: 'Expiry Date must be after Creation Date'
      });
    } else if (isNew) {
      this.createAlert();
    } else if (this.state.id) {
      let groceryExclusiveInfo: ProductInfo[] = [];
      if (isGroceryExclusive) {
        if (customItemName === '' || customItemNumber === '' || (!customImage && !customImageUrl)) {
          this.setState({
            missingFields: [
              customItemName === '' ? 'Item Name(s)' : '',
              customItemNumber === '' ? 'Item Number(s)' : '',
              (!customImage && !customImageUrl) ? 'Product Photos' : ''
            ],
            error: 'Could Not Create Alert: Incomplete Information'
          });
          return;
        }
        let url = groceryExclusiveItems.length > 0 ? groceryExclusiveItems[0].imageUrl : '';
        if (customImage) {
          const imageRes = await savePhoto(customImage);
          // new photo
          if (imageRes.url !== url) url = imageRes.url;
        }
        const newGroceryExclusiveItem = {
          itemNumber: customItemNumber,
          itemName: customItemName,
          productSize: customSize,
          imageUrl: url,
          imageUrlThumb: url
        };
        groceryExclusiveInfo.push(newGroceryExclusiveItem);
        this.setState({ itemInfo: [newGroceryExclusiveItem] });
      }
      this.updateAlert({
        alertNumber,
        additionalInfo: additionalInfo || '',
        expiryDate,
        lotCodes,
        itemNumbers: itemInfo.map(({ itemNumber }) => Number(itemNumber)),
        active,
        type,
        isArchived,
        disposition,
        groceryExclusiveItems: groceryExclusiveInfo
      });
    }
  }

  toggleArchive = () => {
    let {
      isArchived,
      edit,
      additionalInfo
    } = this.state;
    if (edit) {
      this.fetchAlert();
    } else {
      let current = isArchived;
      this.updateAlert({isArchived: !current, active: false, additionalInfo: additionalInfo || ''});
      this.setState({isArchived: !current, active: false});
    }
  }

  async createAlert(): Promise<void> {
    const {
      alertNumber,
      created,
      lotCodes,
      additionalInfo,
      expiryDate,
      isGroceryExclusive,
      customSize,
      customItemName,
      customItemNumber,
      customImage,
      isArchived,
      disposition,
      itemInfo,
      active,
      user,
      type,
      loading,
      itemLoading
    } = this.state;
    let groceryExclusiveInfo: ProductInfo[] = [];
    if (isGroceryExclusive) {
      if (customItemName === '' || customItemNumber === '' || !customImage) {
        this.setState({
          missingFields: [
            customItemName === '' ? 'Item Name(s)' : '',
            customItemNumber === '' ? 'Item Number(s)' : '',
            !customImage ? 'Product Photos' : '',
            !alertNumber ? 'Alert Number' : '', 
            !created ? 'Creation Date' : '', 
            active === undefined ? 'Status of Alert' : '',
            user && user.type === UserType.QAAdmin && (lotCodes === undefined || !(lotCodes.length > 0))   ? 'Lot Codes' : ''
          ],
          error: 'Could Not Create Alert: Incomplete Information'
        });
        return;
      }
      let imageRes = await savePhoto(customImage);
      const { url } = imageRes;
      const newGroceryExclusiveItem = {
        itemNumber: customItemNumber,
        itemName: customItemName,
        productSize: customSize,
        imageUrl: url,
        imageUrlThumb: url
      };
      groceryExclusiveInfo.push(newGroceryExclusiveItem);
      this.setState({ itemInfo: [newGroceryExclusiveItem] });
    }
    
    if (!loading && !itemLoading && alertNumber && created && user && (user.type !== UserType.QAAdmin || lotCodes && lotCodes.length > 0) && active !== undefined) {
      this.setState({loading: true});
      createAlert({
        alertNumber,
        created,
        updated: created,
        additionalInfo: additionalInfo ? additionalInfo : '',
        expiryDate: expiryDate,
        itemInfo: itemInfo.map(({ itemName, itemNumber }) => (
          { itemName: itemName ? itemName : '', itemNumber: itemNumber ? itemNumber : '' }
        )),
        active,
        lotCodes: lotCodes,
        type: type ? type : user.type === UserType.QAAdmin ? AlertType.QAAlert : AlertType.PriceRecall,
        isArchived,
        disposition,
        groceryExclusiveItems: groceryExclusiveInfo,
        toRawPost(): Partial<RawAlert> {
          return {
            active: this.active,
            alertNumber: this.alertNumber,
            expiryDate: this.expiryDate !== undefined ? this.expiryDate.toISO() : undefined,
            itemNumbers: isGroceryExclusive ? [Number(customItemNumber)] : this.itemInfo.map(({ itemNumber }) => Number(itemNumber)),
            type: this.type,
            lotCodes: this.lotCodes,
            additionalInfo: this.additionalInfo ? this.additionalInfo : '',
            disposition: this.disposition,
            groceryExclusiveItems: this.groceryExclusiveItems
          };
        }
      }).then(res => {
        this.setState({error: '', missingFields: [], loading: false, edit: false, isNew: false, redirectToAlert: res.id});
      }).catch(e => {
        let msg = 'Could Not Create Alert';
        if (e && e.response && e.response.status === 409) {
          msg = msg + `: Alert# {${this.state.alertNumber}} already exists.`;
        }
        this.setState({loading: false, error: msg});
      });
    } else if (!loading && !itemLoading) {
      this.setState({
        missingFields: [
          !alertNumber ? 'Alert Number' : '', 
          !created ? 'Creation Date' : '', 
          active === undefined ? 'Status of Alert' : '',
          user && user.type === UserType.QAAdmin && (lotCodes === undefined || !(lotCodes.length > 0))   ? 'Lot Codes' : ''
        ],
        error: 'Could Not Create Alert: Incomplete Information'
      });
    }
  }

  async updateAlert(changes: Partial<ProductAlert>): Promise<void> {
    const {user, alertNumber, lotCodes, id} = this.state;
    if (!alertNumber || (user && user.type === UserType.QAAdmin && (lotCodes === undefined || !(lotCodes.length > 0))) ) {
      this.setState({
        error: 'Could Not Update Alert: Incomplete Information', 
        missingFields: [
          !alertNumber ? 'Alert Number' : '', 
          user && user.type === UserType.QAAdmin && (lotCodes === undefined || !(lotCodes.length > 0)) ? 'Lot Codes' : ''
        ]
      });
    } else if (id) { 
      updateAlert(id, changes)
        .then(res => {
          this.setState({loading: false, edit: false, error: ''}, () => this.fetchAlert());
        })
        .catch(e => {
          let msg = 'Could Not Update Alert';
          this.setState({loading: false, error: msg});
        });
    }
  }

  addItemSlot = () => {
    let current = this.state.newItems;
    current.push({itemName: '', itemNumber: '', imageUrl: '', imageUrlThumb: '', productSize: ''});
    this.setState({newItems: current});
  }

  removeItem = (idx: number) => {
    let current = this.state.newItems;
    current.splice(idx, 1);
    this.setState({newItems: current});
    current = this.state.itemInfo;
    current.splice(idx, 1);
    this.setState({itemInfo: current});
  }

  modifyNewItems = (position: number, itemNumber: string) => {
    let current = this.state.newItems;
    current[position].itemNumber = itemNumber;
    this.setState({newItems: current});
  }

  addLotCode = () => {
    let current = this.state.lotCodes;
    current.push('');
    this.setState({lotCodes: current});
  }

  removeLotCode = (idx: number) => {
    let current = this.state.lotCodes;
    current.splice(idx, 1);
    this.setState({lotCodes: current});
  }

  modifyLotCodes = (i, value) => {
    let current = this.state.lotCodes;
    current[i] = value;
    this.setState({lotCodes: current});
  }

  getItemNames = async () => {
    let current = JSON.parse(JSON.stringify(this.state.newItems));
    // const newNums = current.map(item => item.itemNumber).filter(num => 
    // this.state.itemInfo.map(item => item.itemNumber).indexOf(num) === -1);
    const newNums = current.map(item => item.itemNumber);
    let missing: string[] = [];
    this.setState({itemLoading: true});
    fetchProductInfos(newNums)
      .then((products: ProductInfo[]) => {
        const nameMap = new Map(products.map((item: ProductInfo) => [item.itemNumber, item] as [string, ProductInfo]));
        current.forEach((item: {itemName: string, itemNumber: string}, i: number) => {
          if (nameMap.has(item.itemNumber)) {
            current[i] = nameMap.get(item.itemNumber);
          } else {
            missing.push(item.itemNumber);
          }
        });
        this.setState({ newItems: current, itemInfo: current, missingItems: missing, itemLoading: false });
      })
      .catch(err => {
        this.setState({itemError: 'Could not fetch Item Names', itemLoading: false});
      });
  }

  hideBackModal = () => {
    this.setState({showBackModal: false});
  }

  clickedBack = () => {
    if (this.state.edit) {
      this.setState({showBackModal: true});
    } else {
      this.setState({redirectToAllAlerts: true});
    }
  }

  async fetchUser(): Promise<void> {
    this.setState({
      user: await UserService.fetch(),
    });
  }

  handleCheckboxClick = () => {
    const { isGroceryExclusive, itemInfo } = this.state;
    if (!isGroceryExclusive) {
      if (itemInfo.length > 0 && itemInfo[0].itemNumber !== '') {
        this.setState({ customItemNumber: itemInfo[0].itemNumber });
      }
    }
    this.setState({ isGroceryExclusive: !isGroceryExclusive });
  }

  handleUpload = (newImage: File) => {
    const imageUrl = URL.createObjectURL(newImage);
    this.setState({ 
      customImage: newImage,
      customImageUrl: imageUrl
    });
  }

  handleDelete = () => {
    this.setState({
      customImage: undefined,
      customImageUrl: ''
    });
  }

  componentDidMount() {
    this.fetchUser();
    if (this.props.match && this.props.match.params.id) {
      if (this.props.match.params.id === 'new') {
        this.setState({isNew: true, edit: true, loading: false});
      } else {
        this.fetchAlert();
      }
    } 
  }

  componentDidUpdate(prevProps: any) {
    if (this.props.match && this.props.match.params.id !== prevProps.match.params.id) {
      if (this.props.match.params.id === 'new') {
        this.setState({isNew: true, edit: true, loading: false});
      } else {
        this.fetchAlert();
      }
    }  
  }

  fetchAlert = () => {
    this.setState({ loading: true });
    getAlert(this.props.match.params.id, true)
    .then((res) => {
      if (res.groceryExclusiveItems?.length > 0) {
        const exclusiveItem = res.groceryExclusiveItems[0];
        this.setState({
          isGroceryExclusive: true,
          customItemName: exclusiveItem.itemName,
          customItemNumber: exclusiveItem.itemNumber,
          customSize: exclusiveItem.productSize,
          customImageUrl: exclusiveItem.imageUrl
        });
      }

      fetchProductInfos(res.itemInfo.map(({itemNumber}) => (itemNumber)))
        .then((products: ProductInfo[]) => {
          this.setState({
            ...res,
            loading: false,
            edit: false,
            error: '',
            itemInfo: products,
            newItems: JSON.parse(JSON.stringify(products)),
            expiryDate: res.expiryDate.plus({hours: 5})
          });
        })
        .catch(err => {
          this.setState({itemError: 'Could not fetch Item Names', edit: false, itemLoading: false, loading: false});
        });
    })
    .catch(() => {
      this.setState({ error: 'Could Not Find Alert', edit: false, itemLoading: false, loading: false });
    });
  }

  render() {
    const {
      alertNumber,
      active,
      expiryDate,
      itemInfo,
      newItems,
      isNew,
      created,
      updated,
      lotCodes,
      isGroceryExclusive,
      customSize,
      customItemName,
      customItemNumber,
      customImageUrl,
      additionalInfo,
      isArchived,
      disposition,
      loading,
      itemLoading,
      error,
      edit,
      missingItems,
      missingFields,
      user
    } = this.state;

    const photoError = isGroceryExclusive && missingFields.indexOf('Product Photos') !== -1;
    // const DATE_FORMAT = 'yyyy-MM-dd hh:mm';
    const CHAR_LIMIT = 250;

    const ProductTile = (product: ProductInfo) => {
      return (
        <div className="tile">
          <div className="imagery"><img src={product.imageUrlThumb}/></div>
          <div>
            <h5>{product.itemName}</h5>
            <h6>{product.productSize}</h6>
            <h6>ID: #{product.itemNumber}</h6>
          </div>
        </div>
      );
    };

    const dispositionValue = (disp) => {
      if (disp === 'destroy') {
        return 'Destroy Onsite';
      } else if (disp === 'return') {
        return 'Return to LCBO';
      } else {
        return 'Return or Destroy';
      }
    };

    const exclusionMap = {
      [UserType.Admin]: ['Lot Codes']
    };

    const infoMap = [
      {
        label: 'Alert Number',
        required: true,
        edit: (
          <input
            type="text"
            pattern="[0-9]*"
            value={alertNumber || ''}
            onChange={(e) => {
              this.setState({
                alertNumber: parseInt(e.target.value, 10)
              });
            }}
          />
        ),
        value: <div>{alertNumber}</div>
      },
      { 
        label: 'Status of Alert', 
        required: true,
        edit: 
          (
            <div>
              <select value={active !== undefined ? active.toString() : ''} onChange={this.setStatus}>
                <option
                  value=""
                  disabled={true}
                  selected={active === undefined}
                  hidden={true}
                >
                  Select an Option...
                </option>
                <option value={'true'} selected={active === true}>Active</option>
                <option value={'false'} selected={active === false}>Inactive</option>
              </select>
            </div>
          ),
        value: <div>{isArchived ? 'Archived' : active ? 'Active' : 'Inactive'}</div>
      },
      {
        label: 'Grocer\'s Options',
        required: true,
        edit: 
          (
            <div>
              <select value={disposition ? disposition : ''} onChange={this.setDisposition}>
                <option
                  value=""
                  disabled={true}
                  selected={!!disposition}
                  hidden={true}
                >
                  Select an Option...
                </option>
                <option value={'destroy'} selected={disposition === 'destroy'}>Destroy Onsite</option>
                <option value={'return'} selected={disposition === 'return'}>Return to LCBO</option>
                <option value={'both'} selected={disposition === 'both'}>Return or Destroy</option>
              </select>
            </div>
          ),
        value: <div>{dispositionValue(disposition)}</div>
      },
      {
        label: 'Item Number(s)',
        required: true,
        edit: (
          <><div className="header">
            <div>
              {!isGroceryExclusive ? 
              (newItems && newItems.map((item, i) =>
                <>
                  <input
                    key="item"
                    className={`item-number${missingItems.indexOf(item.itemNumber) !== -1 ? '-error' : ''}`}
                    value={item.itemNumber}
                    onChange={(e) => { this.modifyNewItems(i, e.target.value); }}
                  />
                  <button onClick={() => this.removeItem(i)}><strong >X</strong></button>
                  {newItems.length !== i + 1 && ' , '}
                </>))
                :
                <input
                  key="customItemNumber"
                  className={'item-number'}
                  value={customItemNumber}
                  onChange={(e) => {
                    this.setState({
                      customItemNumber: e.target.value
                    });
                  }}
                />
              }
            </div>
            {!isGroceryExclusive && 
            <div>
              <button onClick={this.addItemSlot}><strong >+</strong></button>
              {/* {newItems.length ? 
                <button onClick={this.removeLastItem}><strong >-</strong></button> : ''
              } */}
              {newItems.length ?
                <button onClick={this.getItemNames}><strong >🔎</strong></button> : ''
              }
            </div>}
          </div>
          {!this.state.isGroceryExclusive && missingItems.length > 0 && <div className="item-number-error">
              Could not find items: {missingItems.join(', ')}
          </div>}</>
        ),
        value:
          <div>{newItems && newItems.map((item) => item.itemNumber).join(', ')}</div>,
      },
      {
        label: 'Item Name(s)',
        required: true,
        edit:
          isGroceryExclusive ?
          (
          <div>
            <input
              key="customItemName"
              value={customItemName || ''}
              onChange={(e) => {
                this.setState({
                  customItemName: e.target.value
                });
              }}
            />
          </div>)
          :
          (
          <div>
            {newItems && newItems.map((item) => `${item.itemName} (${item.productSize})`).join(', ')}
            <LoadHolder loading={itemLoading} />
          </div>)
        ,
        value: (
          <>
            <div>{newItems && newItems.map((item) =>
              `${item.itemName} (${item.productSize})`).join(', ')}
            </div>
            <div className="tile-array">
              {newItems.map(item => ProductTile(item))}
            </div>
          </>),
      },
      {
        label: 'Additional Information',
        required: false,
        edit: (
          <textarea
            rows={5}
            style={{ width: '75%' }}
            value={additionalInfo || ''} 
            maxLength={CHAR_LIMIT}
            onChange={e => this.setState({additionalInfo: e.target.value})}
          />
        ),
        value: <div>{additionalInfo}</div>
      },
      {
        label: 'Size',
        required: false,
        edit: (
          <input
            type="text"
            value={customSize || ''}
            onChange={(e) => {
              this.setState({
                customSize: e.target.value
              });
            }}
          />
        ),
        value: <div>{customSize}</div>
      },
      {
        label: 'Lot Codes',
        required: true,
        edit: (
          <div className="header">
            <div>
              {lotCodes.map((code, i) =>
                <>
                  <input
                    key="code"
                    className={`lot-code`}
                    value={code}
                    onChange={(e) => { this.modifyLotCodes(i, e.target.value); }}
                  />
                  <button onClick={() => this.removeLotCode(i)}><strong >X</strong></button>
                  {lotCodes.length !== i + 1 && ' , '}
                </>)
              }
            </div>
            <div>
              <button onClick={this.addLotCode}><strong >+</strong></button>
              {/* {newItems.length ? 
                <button onClick={() => this.removeLotCode(i)}><strong >-</strong></button> : ''
              } */}
            </div>
          </div>
        ),
        value: <div>{lotCodes ? lotCodes.join(', ') : ''}</div>
      },
      {
        label: 'Alert Expiry Date',
        required: true,
        edit: (
          <>
          <input
            type="date"
            onChange={(e) => { console.log(DateTime.fromISO(e.target.value)); this.setState({ expiryDate: DateTime.fromISO(e.target.value), active: true }); }}
            value={
              expiryDate ?
                expiryDate.toISODate() :
                undefined
            }
            readOnly={false}
          />
          </>
        ),
        value: (
          <div>{!expiryDate ||  expiryDate.toLocaleString(DateTime.DATE_FULL) === 'Invalid DateTime' ? '' : expiryDate.toLocaleString(DateTime.DATE_FULL)}
          </div>
        ),
      },
    ];
      
    return (
    <>
      <div className="alert">
      {loading ? <LoadHolder loading={loading}/> : <><div className="header">
          {this.state.redirectToAlert && (
            <Redirect
              push={true}
              to={{
                pathname: `${
                  this.state.redirectToAlert
                }`,
              }}
            />
          )}
          {this.state.redirectToAllAlerts && (
            <Redirect
              push={true}
              to={{
                pathname: '../alerts',
              }}
            />
          )}
          <Modal visible={this.state.showBackModal} onClose={this.hideBackModal}>
            <div className="claim-modal">
              <h3>Your changes have not been saved. Are you sure you want to go back?</h3>
              <div className="actions-info-modal-input">
                <button id="toast-cancel-button" disabled={loading} onClick={() => this.hideBackModal()}>
                  <LoadHolder loading={loading}>Continue Editing</LoadHolder>
                </button>
                <button
                  id="toast-delete-button"
                  disabled={loading}
                  onClick={() => this.setState({ redirectToAllAlerts: true })}
                >
                  <LoadHolder loading={loading}>Discard {isNew ? ' Alert' : ' Changes'}</LoadHolder>
                </button>
              </div>
            </div>
          </Modal>
          <div className="back" onClick={this.clickedBack}>
            <MdArrowBack />
            {' Back to all Alerts'}
          </div>
          <div className="buttons">
            <h1>
              {edit && !isNew && <TiPencil />}
            </h1>
            {!isArchived && <button onClick={this.toggleEdit} className="edit">
              <h2>{edit ? isNew ? 'Save Alert' : 'Save Changes' : 'Edit Alert'}</h2>
            </button>}
            {!isNew && <button onClick={this.toggleArchive} className="delete">
              <h2>{edit ? 'Discard Changes' : isArchived ? 'Reactivate' : 'Archive'}</h2>
            </button>}
          </div>
        </div>
        <br />
        <div className="header">
          <div>
            <h1>Alert {alertNumber !== undefined && !isNaN(alertNumber) ?
              '#' + alertNumber.toString() : <h4> &nbsp; NEW</h4>}
            </h1>
          </div>
          {error.length > 0 && <div className="error-text">*{error}</div>}
        </div>
        <div className="details">
          <div className="info">
            <h2>Basic Information</h2>
            <div className="createDate">
              {created ? 'Date Created: ' + created.toLocaleString(DateTime.DATETIME_FULL) : null}
            </div>
            <div className="card">
              <div>
                {infoMap.map((info) => {
                  return (
                    <>
                      {(user !== undefined && (Object.keys(exclusionMap).indexOf(user.type) === -1 || exclusionMap[user.type].indexOf(info.label) === -1)) &&
                       (!info.label.includes('Size') || isGroceryExclusive) &&
                      <div key={info.label} className="row">
                        <div className={`key${missingFields.indexOf(info.label) !== -1 ? '-error' : ''}`}>
                          {info.label}{info.required && <span style={{ color: '#B1243C' }}>*</span>}
                          {info.label.includes('Item Number') && 
                            <div className="grocery-exclusive-checkbox">
                              <input 
                                type="checkbox"
                                id="groceryExclusiveCheckbox"
                                name="groceryExclusiveCheckbox"
                                disabled={!isNew}
                                onClick={this.handleCheckboxClick}
                                checked={isGroceryExclusive}
                              />
                              <span className="grocery-exclusive">
                                This is a Grocery Exclusive Item
                              </span>
                            </div>
                          }
                        </div>
                        <div className="value">{edit ? info.edit : info.value}</div>
                      </div>}
                    </>
                  );
                })}
              </div>
              <div className="updated">
                <h3>Last Edited: {updated ?
                  updated.toLocaleString(DateTime.DATETIME_FULL) : DateTime.local()}</h3>
              </div>
            </div>
          </div>
          <div className="photos">
            <h2>Product Photos {isGroceryExclusive && <span style={{ color: 'red' }}> * </span>}</h2>
            <hr />
            {(isGroceryExclusive && edit) ?
              <ImageUploader
                image={customImageUrl}
                onUpload={this.handleUpload}
                onDelete={this.handleDelete}
                viewOnly={!edit}
                error={photoError}
              />
            :
            itemInfo.map(item => 
              <div key="item" className="itemPhoto" style={{ width: '100%' }}><img src={item.imageUrl} /></div>
            )}
          </div>
        </div></>}
      </div>
    </>
    );
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/Alert/index.tsx